import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { UIState, MapState, ITextLabel } from '../types';
import { ApiService } from '../services/api';
import { RootState } from './index';

// Helper function to create ApiService with current token
const createApiService = (getState: () => RootState) => {
    const state = getState();
    const apiService = new ApiService(
        process.env.REACT_APP_API_URL || 'http://localhost:3051',
        process.env.REACT_APP_API_URL || 'http://localhost:3051/api'
    );
    // Use token from Redux state, or fallback to localStorage if state hasn't been hydrated yet
    const token = state.auth.token || localStorage.getItem('token');
    if (token) {
        apiService.setToken(token);
    }
    return apiService;
};

const initialUIState: UIState = {
    language: 'en',
    isRTL: false,
    isEditMode: false,
    selectedDeviceMac: null,
    selectedGatewayMac: null,
    theme: 'light',
    showDeviceNames: true,
    showGatewayCoverage: true,
    showGridLines: false,
    showRSSIValues: false,
    showLastSeen: true,
    autoZoom: true,
    isUtilityCardMinimized: false,
    activeUtilityTab: 'maps' as 'maps' | 'devices' | 'gateways' | 'view' | 'settings',
};

interface MapStateWithLoading extends MapState {
    loading?: boolean;
    error?: string | null;
}

const initialMapState: MapStateWithLoading = {
    labels: [],
    zoom: 1,
    center: [0, 0],
    loading: false,
    error: null,
};

// Async thunks for text labels
export const fetchTextLabels = createAsyncThunk(
    'map/fetchTextLabels',
    async (_, { getState }) => {
        const apiService = createApiService(getState as () => RootState);
        return await apiService.fetchTextLabels();
    }
);

export const saveTextLabel = createAsyncThunk(
    'map/saveTextLabel',
    async (label: ITextLabel, { getState }) => {
        const apiService = createApiService(getState as () => RootState);
        if (label._id) {
            return await apiService.updateTextLabel(label.id, label);
        } else {
            const { _id, createdAt, updatedAt, ...labelData } = label;
            return await apiService.createTextLabel(labelData);
        }
    }
);

export const deleteTextLabel = createAsyncThunk(
    'map/deleteTextLabel',
    async (id: string, { getState }) => {
        const apiService = createApiService(getState as () => RootState);
        await apiService.deleteTextLabel(id);
        return id;
    }
);

const uiSlice = createSlice({
    name: 'ui',
    initialState: initialUIState,
    reducers: {
        setLanguage: (state, action: PayloadAction<'en' | 'he'>) => {
            state.language = action.payload;
            state.isRTL = action.payload === 'he';
        },
        toggleEditMode: (state) => {
            state.isEditMode = !state.isEditMode;
        },
        setEditMode: (state, action: PayloadAction<boolean>) => {
            state.isEditMode = action.payload;
        },
        setSelectedDevice: (state, action: PayloadAction<string | null>) => {
            state.selectedDeviceMac = action.payload;
        },
        setSelectedGateway: (state, action: PayloadAction<string | null>) => {
            state.selectedGatewayMac = action.payload;
        },
        setTheme: (state, action: PayloadAction<'light' | 'dark'>) => {
            state.theme = action.payload;
        },
        setViewOption: (state, action: PayloadAction<{ key: keyof Pick<UIState, 'showDeviceNames' | 'showGatewayCoverage' | 'showGridLines' | 'showRSSIValues' | 'showLastSeen' | 'autoZoom'>, value: boolean }>) => {
            state[action.payload.key] = action.payload.value;
        },
        toggleUtilityCardMinimized: (state) => {
            state.isUtilityCardMinimized = !state.isUtilityCardMinimized;
        },
        setActiveUtilityTab: (state, action: PayloadAction<'maps' | 'devices' | 'gateways' | 'view' | 'settings'>) => {
            state.activeUtilityTab = action.payload;
        },
    },
});

const mapSlice = createSlice({
    name: 'map',
    initialState: initialMapState,
    reducers: {
        setZoom: (state, action: PayloadAction<number>) => {
            // Check if value has actually changed to prevent infinite loops
            if (Math.abs(state.zoom - action.payload) < 0.001) {
                return; // No actual change, skip state update
            }
            state.zoom = action.payload;
        },
        setCenter: (state, action: PayloadAction<[number, number]>) => {
            // Check if values have actually changed to prevent infinite loops
            if (state.center &&
                Math.abs(state.center[0] - action.payload[0]) < 0.01 &&
                Math.abs(state.center[1] - action.payload[1]) < 0.01) {
                return; // No actual change, skip state update
            }
            state.center = action.payload;
        },
        addLabel: (state, action: PayloadAction<ITextLabel>) => {
            state.labels.push(action.payload);
        },
        updateLabel: (state, action: PayloadAction<ITextLabel>) => {
            const index = state.labels.findIndex(label => label.id === action.payload.id);
            if (index !== -1) {
                state.labels[index] = action.payload;
            }
        },
        removeLabel: (state, action: PayloadAction<string>) => {
            state.labels = state.labels.filter(label => label.id !== action.payload);
        },
    },
    extraReducers: (builder) => {
        builder
            // Fetch text labels
            .addCase(fetchTextLabels.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchTextLabels.fulfilled, (state, action) => {
                state.loading = false;
                state.labels = action.payload;
            })
            .addCase(fetchTextLabels.rejected, (state, action) => {
                state.loading = false;
                state.error = action.error.message || 'Failed to fetch text labels';
            })
            // Save text label
            .addCase(saveTextLabel.fulfilled, (state, action) => {
                const index = state.labels.findIndex(label => label.id === action.payload.id);
                if (index !== -1) {
                    state.labels[index] = action.payload;
                } else {
                    state.labels.push(action.payload);
                }
            })
            // Delete text label
            .addCase(deleteTextLabel.fulfilled, (state, action) => {
                state.labels = state.labels.filter(label => label.id !== action.payload);
            });
    },
});

export const { setLanguage, toggleEditMode, setEditMode, setSelectedDevice, setSelectedGateway, setTheme, setViewOption, toggleUtilityCardMinimized, setActiveUtilityTab } = uiSlice.actions;
export const { setZoom, setCenter, addLabel, updateLabel, removeLabel } = mapSlice.actions;

export const uiReducer = uiSlice.reducer;
export const mapReducer = mapSlice.reducer;
